/**
 * Sensitivity Analysis Section for Accountant Report View
 *
 * Forensic integrity: this panel renders ONLY the deterministic engine's
 * sensitivity sweep (POST /api/v1/reports/{id}/sensitivity, computed by
 * backend/app/services/sensitivity_analysis.py). It never fabricates a range
 * by multiplying the claim by hardcoded ±% bands. If the engine has not run,
 * it shows an honest unavailable state with a pointer to run the analysis.
 */

import React from 'react';

interface SensitivityResult {
  base_claim?: number;
  parameter?: string;
  results?: Array<{ pct: number; claim: number; change_pct?: number }>;
  range?: { min: number; max: number; spread: number };
  run_at?: string;
}

interface SensitivityAnalysisSectionProps {
  /** Real engine output from report.sensitivity_analysis (null/undefined if not yet run). */
  sensitivityAnalysis?: SensitivityResult | null;
  reportId?: string;
  formatCurrency: (amount?: number) => string;
}

export default function SensitivityAnalysisSection({
  sensitivityAnalysis,
  reportId,
  formatCurrency
}: SensitivityAnalysisSectionProps) {
  const sa = sensitivityAnalysis;
  const hasRealResult =
    !!sa &&
    typeof sa.base_claim === 'number' &&
    !!sa.range &&
    typeof sa.range.min === 'number' &&
    typeof sa.range.max === 'number';

  const baseClaim = sa?.base_claim ?? 0;
  const min = sa?.range?.min ?? 0;
  const max = sa?.range?.max ?? 0;
  const spread = sa?.range?.spread ?? Math.max(0, max - min);
  const spreadPct = baseClaim > 0 ? (spread / baseClaim) * 100 : 0;
  const paramLabel = (sa?.parameter ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());

  return (
    <div
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6 mb-6"
    >
      <h2 className="cs-h2 mb-4 flex items-center text-[var(--color-text-primary)]">
        Sensitivity Analysis - Range of Outcomes
      </h2>
      <p className="text-sm mb-6 text-[var(--color-text-secondary)]">
        Shows how the calculated claim changes when a key input is varied by the deterministic
        engine. A smaller spread indicates a more stable, defensible calculation.
      </p>

      {hasRealResult ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4"
            >
              <p className="text-xs font-medium mb-1 text-[var(--color-text-secondary)]">ENGINE LOW</p>
              <p className="text-sm mb-2 font-semibold text-[var(--color-text-primary)]">Minimum Outcome</p>
              <p className="text-2xl font-bold mb-2 font-mono tabular-nums text-right text-[var(--color-needs-human-text)]">
                {formatCurrency(min)}
              </p>
              <div className="text-xs rounded px-2 py-1 text-[var(--color-needs-human-text)] bg-[var(--color-needs-human-tint)]">
                Lowest claim across the sweep
              </div>
            </div>

            <div
              className="rounded-md p-4 border"
              style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
            >
              <p className="text-xs font-medium mb-1" style={{ color: 'var(--color-verified)' }}>BASE CLAIM</p>
              <p className="text-sm mb-2 font-semibold" style={{ color: 'var(--color-verified)' }}>Calculated</p>
              <p className="text-2xl font-bold mb-2 font-mono tabular-nums text-right" style={{ color: 'var(--color-verified)' }}>
                {formatCurrency(baseClaim)}
              </p>
              <div className="text-xs rounded px-2 py-1" style={{ color: 'var(--color-verified)', backgroundColor: 'transparent' }}>
                Deterministic claim amount
              </div>
            </div>

            <div
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4"
            >
              <p className="text-xs font-medium mb-1 text-[var(--color-text-secondary)]">ENGINE HIGH</p>
              <p className="text-sm mb-2 font-semibold text-[var(--color-text-primary)]">Maximum Outcome</p>
              <p className="text-2xl font-bold mb-2 font-mono tabular-nums text-right text-[var(--color-needs-human-text)]">
                {formatCurrency(max)}
              </p>
              <div className="text-xs rounded px-2 py-1 text-[var(--color-needs-human-text)] bg-[var(--color-needs-human-tint)]">
                Highest claim across the sweep
              </div>
            </div>
          </div>

          <div
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4"
          >
            <h3 className="cs-h3 mb-3 flex items-center text-[var(--color-text-primary)]">
              Parameter Sweep{paramLabel ? `: ${paramLabel}` : ''}
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-[var(--color-text-secondary)]">Spread (max − min):</span>
                <span className="font-mono font-semibold text-right tabular-nums text-[var(--color-text-primary)]">
                  {formatCurrency(spread)} ({spreadPct.toFixed(1)}%)
                </span>
              </div>
              {(sa?.results ?? []).map((pt) => (
                <div className="flex justify-between items-center" key={pt.pct}>
                  <span className="text-[var(--color-text-secondary)]">
                    {pt.pct > 0 ? '+' : ''}{pt.pct}% variation:
                  </span>
                  <span className="font-mono font-semibold text-right tabular-nums text-[var(--color-text-primary)]">
                    {formatCurrency(pt.claim)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {sa?.run_at && (
            <p className="text-xs mt-3 text-[var(--color-text-dim)]">
              Engine run: {new Date(sa.run_at).toLocaleString()}
            </p>
          )}
        </>
      ) : (
        <div
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-6 text-center"
        >
          <p className="text-base font-semibold mb-2 text-[var(--color-text-primary)]">
            Not available — run the deterministic analysis
          </p>
          <p className="text-sm mb-4 text-[var(--color-text-secondary)]">
            No sensitivity sweep has been computed for this report. The range of outcomes must be
            produced by the deterministic engine — it is not estimated from the claim amount.
            Run the sensitivity analysis to populate this panel with real low/base/high values.
          </p>
          {reportId && (
            <a
              href={`/reports/${reportId}#sensitivity`}
              className="inline-flex items-center gap-2 text-sm font-semibold rounded-md px-4 py-2 bg-[var(--color-brand)] text-[var(--color-on-brand)]"
            >
              Run Sensitivity Analysis
            </a>
          )}
        </div>
      )}
    </div>
  );
}
